// src/hooks/useBanners.ts - Simplified version
import {useCallback, useMemo} from "react";
import {
  useGetBannersQuery,
  useGetBannerByIdQuery,
  useCreateBannerMutation,
  useUpdateBannerMutation,
  useDeleteBannerMutation,
  useUpdateBannerWithImageMutation,
} from "../store/api/bannersApi";
import {useToast} from "./common/useToast";
import {useConfirm} from "./common/useConfirm";

interface CreateBannerData {
  file: File;
  title?: string;
  description?: string;
}

interface UpdateBannerData {
  title?: string;
  description?: string;
}

export const useBanners = () => {
  const {success, error} = useToast();
  const {confirm} = useConfirm();

  // Query hooks
  const {
    data: banners = [],
    isLoading,
    isFetching,
    error: fetchError,
    refetch,
  } = useGetBannersQuery();

  // Mutation hooks
  const [createBanner, {isLoading: isCreating}] = useCreateBannerMutation();
  const [updateBanner, {isLoading: isUpdating}] = useUpdateBannerMutation();
  const [deleteBanner, {isLoading: isDeleting}] = useDeleteBannerMutation();
  const [updateBannerWithImage, {isLoading: isUpdatingWithImage}] =
    useUpdateBannerWithImageMutation();

  // Simple stats from banner data
  const stats = useMemo(() => {
    const total = banners.length;
    const maxAllowed = 4;
    const canCreate = total < maxAllowed;

    return {
      total,
      maxAllowed,
      canCreate,
      remaining: maxAllowed - total,
    };
  }, [banners]);

  // Handlers
  const handleCreate = useCallback(
    async (data: CreateBannerData) => {
      // Check banner limit before creating
      if (banners.length >= 4) {
        error("Error", "Maximum 4 banners allowed");
        return false;
      }

      try {
        await createBanner(data).unwrap();
        success("Success", "Banner created successfully");
        return true;
      } catch (err: any) {
        const errorMessage = err?.data?.message || "Failed to create banner";
        error("Error", errorMessage);
        return false;
      }
    },
    [createBanner, success, error, banners.length]
  );

  const handleUpdate = useCallback(
    async (id: number, data: UpdateBannerData) => {
      try {
        await updateBanner({id, ...data}).unwrap();
        success("Success", "Banner updated successfully");
        return true;
      } catch (err: any) {
        const errorMessage = err?.data?.message || "Failed to update banner";
        error("Error", errorMessage);
        return false;
      }
    },
    [updateBanner, success, error]
  );
  const handleUpdateWithImage = useCallback(
    async (
      id: number,
      file: File,
      data?: {title?: string; description?: string}
    ) => {
      console.log(
        "🔄 Updating banner with image ID:",
        id,
        "file:",
        file.name,
        "data:",
        data
      );

      try {
        const payload: {
          id: number;
          file: File;
          title?: string;
          description?: string;
        } = {
          id,
          file,
        };
        if (data?.title !== undefined) payload.title = data.title;
        if (data?.description !== undefined)
          payload.description = data.description;

        const result = await updateBannerWithImage(payload).unwrap();
        console.log("✅ Update with image result:", result);
        success("Success", "Banner updated successfully");
        return true;
      } catch (err: any) {
        console.error("❌ Update with image error:", err);
        const errorMessage = err?.data?.message || "Failed to update banner";
        error("Error", errorMessage);
        return false;
      }
    },
    [updateBannerWithImage, success, error]
  );

  const handleDelete = useCallback(
    async (id: number) => {
      const confirmed = await confirm({
        title: "Delete Banner",
        message:
          "Are you sure you want to delete this banner? This action cannot be undone.",
        confirmText: "Delete",
        cancelText: "Cancel",
      });

      if (!confirmed) return false;

      try {
        await deleteBanner(id).unwrap();
        success("Success", "Banner deleted successfully");
        return true;
      } catch (err: any) {
        const errorMessage = err?.data?.message || "Failed to delete banner";
        error("Error", errorMessage);
        return false;
      }
    },
    [deleteBanner, success, error, confirm]
  );

  return {
    // Data
    banners,
    stats,

    // Loading states
    isLoading,
    isFetching,
    isCreating,
    isUpdating,
    isDeleting,

    // Error
    fetchError,

    // Actions
    handleCreate,
    handleUpdate,
    handleUpdateWithImage,
    handleDelete,
    refetch,
  };
};

// Helper hook for single banner
export const useBanner = (id: number) => {
  const {
    data: banner,
    isLoading,
    error,
    refetch,
  } = useGetBannerByIdQuery(id, {
    skip: !id,
  });

  return {
    banner,
    isLoading,
    error,
    refetch,
  };
};
